import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2, Printer, Camera,
  Truck, Save, Package, FileText } from "lucide-react";
import SearchableSelect from "../components/SearchableSelect";
import ConfirmDialog from "../components/ConfirmDialog";
import AuthImage from "../components/AuthImage";
import { useAuth } from "../lib/auth";

const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700",
  confirmed: "bg-blue-50 text-blue-700",
  packing: "bg-amber-50 text-amber-700",
  dispatched: "bg-green-50 text-green-700",
};

export default function PurchaseOrders() {
  const { can, user } = useAuth();
  const [pos, setPos] = useState([]);
  const [bills, setBills] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/purchase-orders").then(r => setPos(r.data));
  useEffect(() => { load(); api.get("/billing_companies").then(r => setBills(r.data)); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this PO?")) return;
    try { await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6 md:space-y-8" data-testid="po-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Orders</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#2D2622]">Purchase Orders.</h1>
          <p className="text-[#5D5148] mt-2 text-sm max-w-xl">
            Create → pack (with photo proof per lot) → dispatch to reduce stock.
          </p>
        </div>
        {can("po.create") && (
          <button onClick={() => setShowNew(true)} data-testid="po-new-btn"
            className="h-11 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#5D4A2E]">
            <Plus size={14}/> New PO
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pos.length === 0 && (
          <div className="col-span-full border border-dashed border-[#D4C5B0] p-8 md:p-12 text-center text-[#8B6F47]">
            No purchase orders yet.
          </div>
        )}
        {pos.map(po => (
          <div key={po.id} className="bg-white border border-[#E5D8C4] p-4 md:p-5 hover:border-[#2D2622]">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold truncate">{po.billing_company_name}</div>
                <div className="font-display font-bold text-xl text-[#2D2622] mt-1">{po.po_no}</div>
                {po.estimate_no && <div className="text-xs text-[#8B6F47] font-mono2 mt-0.5">Est. {po.estimate_no}</div>}
              </div>
              <div className={`px-2 py-1 text-[10px] uppercase tracking-widest font-semibold flex-shrink-0 ${STATUS_STYLES[po.status] || "bg-stone-100"}`}>
                {po.status}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#F3ECDF] text-xs font-mono2 text-[#5D5148] flex justify-between">
              <span>Ordered · {po.order_date}</span>
              <span>Due · {po.expected_delivery || "—"}</span>
            </div>
            <div className="mt-2 text-xs text-[#5D5148]">
              {po.lines?.length || 0} line{po.lines?.length===1?"":"s"} · {po.lines?.reduce((s,l)=>s+l.quantity,0)} pcs
            </div>
            {(po.warehouse_assignments || []).length > 0 && (
              <div className="mt-2 text-[10px] text-[#8B6F47] font-mono2">
                {po.warehouse_assignments.map(w => `${w.warehouse_place}: ${(w.assignee_names || []).join(', ') || '—'}`).join(' · ')}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button onClick={() => setSelected(po)} data-testid={`po-open-${po.id}`}
                className="flex-1 h-9 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">
                Open
              </button>
              {can("po.edit") && (po.status !== "dispatched" || user?.role === "admin") && (
                <button onClick={() => setEditing(po)} data-testid={`po-edit-${po.id}`}
                  className="h-9 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">
                  Edit
                </button>
              )}
              {can("po.delete") && !["confirmed","packing","dispatched","completed"].includes(po.status) && (
                <button onClick={() => del(po.id)} data-testid={`po-delete-${po.id}`}
                  className="p-2 text-[#8B6F47] hover:text-red-600"><Trash2 size={14}/></button>
              )}
              {po.status === "dispatched" && (
                <span data-testid={`po-locked-${po.id}`}
                  className="px-2 py-1 bg-[#F3ECDF] text-[10px] uppercase tracking-widest text-[#8B6F47] inline-flex items-center gap-1">
                  🔒 Dispatched
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <POEditor bills={bills} onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }} />
      )}
      {editing && (
        <POEditor bills={bills} initial={editing} editId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {selected && (
        <PODetail poId={selected.id} onClose={() => setSelected(null)}
          onChanged={load} />
      )}
    </div>
  );
}

/* ------------- New PO editor ------------- */
function POEditor({ onClose, onSaved, bills, initial, editId }) {
  const [f, setF] = useState(() => initial ? {
    po_no: initial.po_no || "",
    estimate_no: initial.estimate_no || "",
    billing_company_id: initial.billing_company_id || bills[0]?.id || "",
    order_date: initial.order_date || new Date().toISOString().slice(0,10),
    expected_delivery: initial.expected_delivery || "",
    notes: initial.notes || "",
    lines: (initial.lines || []).map(l => ({
      _key: (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random())),
      category_id: l.category_id, color_id: l.color_id, size_id: l.size_id,
      quantity: l.quantity, allocations: l.allocations || [],
      notes: l.notes || "",
    })),
    warehouse_assignments: (initial.warehouse_assignments || []).map(w => ({
      warehouse_place: w.warehouse_place, assignee_ids: w.assignee_ids || []
    })),
    custom_fields: initial.custom_fields || {},
  } : {
    po_no: "", estimate_no: "", billing_company_id: bills[0]?.id || "",
    order_date: new Date().toISOString().slice(0,10),
    expected_delivery: "", notes: "", lines: [], warehouse_assignments: [],
    custom_fields: {},
  });
  const [cats, setCats] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/users/assignees").then(r => setAssignees(r.data)).catch(() => {});
    api.get("/warehouses").then(r => setWarehouses(r.data)).catch(() => {});
    api.get("/custom-fields", { params: { form_scope: "purchase_order" } })
      .then(r => setCustomFields((r.data || []).filter(fd => !fd.hidden)))
      .catch(() => {});
  }, []);
  const addLine = () => setF({ ...f, lines: [...f.lines, {
    // Blank quantity by default — the operator types the exact figure. The
    // `""` (empty string) is coerced to a number at submit / validate time.
    // `_key` is a client-side stable id so React can track this row across
    // reorders / mid-list removals without misattributing state.
    _key: (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random())),
    category_id: "", color_id: "", size_id: "", quantity: "",
    allocations: [], notes: "",
  }]});
  const rmLine = (i) => setF({ ...f, lines: f.lines.filter((_, idx) => idx !== i) });
  // "Add another size" — clones the current line's category + colour so the
  // operator can add multiple sizes for the same material without re-picking
  // both dropdowns. Blanks the size + quantity + allocations of the new row.
  const addSizeFromLine = (i) => {
    const src = f.lines[i];
    const clone = {
      _key: (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random())),
      category_id: src.category_id, color_id: src.color_id,
      size_id: "", quantity: "", allocations: [], notes: "",
    };
    const lines = [...f.lines];
    lines.splice(i + 1, 0, clone);
    setF({ ...f, lines });
  };

  // Detect warehouses actually used across the PO's allocated lots
  const usedWarehouses = useMemo(() => {
    const s = new Set();
    f.lines.forEach(l => (l.allocations || []).forEach(a => {
      if (a.warehouse_place) s.add(a.warehouse_place);
    }));
    return [...s];
  }, [f.lines]);

  const setAssignForWH = (wh, ids) => {
    const wa = [...(f.warehouse_assignments || [])];
    const i = wa.findIndex(w => w.warehouse_place === wh);
    if (i >= 0) wa[i] = { warehouse_place: wh, assignee_ids: ids };
    else wa.push({ warehouse_place: wh, assignee_ids: ids });
    setF({ ...f, warehouse_assignments: wa });
  };

  const submit = async () => {
    if (!f.po_no.trim()) { toast.error("PO number required"); return; }
    if (f.lines.length === 0) { toast.error("Add at least one line"); return; }
    // Coerce every quantity to an integer at submit time — the input holds a
    // string ("" while blank) so we can render a truly empty field by default.
    const normLines = f.lines.map(l => ({
      ...l, quantity: parseInt(l.quantity || "0", 10) || 0,
    }));
    for (let i = 0; i < normLines.length; i++) {
      const l = normLines[i];
      if (!l.category_id || !l.color_id || !l.size_id) {
        toast.error(`Line ${i + 1}: pick category, colour and size`); return;
      }
      if (!l.quantity || l.quantity < 1) {
        toast.error(`Line ${i + 1}: quantity is required`); return;
      }
      const alloc = (l.allocations || []).reduce((s, a) => s + (a.quantity || 0), 0);
      if (alloc > l.quantity) {
        toast.error(`Line ${i + 1}: allocated quantity (${alloc}) exceeds ordered quantity (${l.quantity}). Please correct the allocation.`);
        return;
      }
    }
    const payload = { ...f, lines: normLines };
    for (const cf of customFields) {
      if (cf.required && (f.custom_fields?.[cf.key] === undefined ||
          f.custom_fields?.[cf.key] === "" || f.custom_fields?.[cf.key] === null)) {
        toast.error(`${cf.label} is required`); return;
      }
    }
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/purchase-orders/${editId}`, payload);
        toast.success(`PO ${f.po_no} updated`);
        onSaved({ ...payload, id: editId });
      } else {
        const r = await api.post("/purchase-orders", payload);
        toast.success(`PO ${r.data.po_no} created`);
        onSaved(r.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl border border-[#E5D8C4] my-4" onClick={(e)=>e.stopPropagation()}>
        <div className="p-4 md:p-5 border-b border-[#F3ECDF] flex justify-between items-center bg-white">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">New</div>
            <div className="font-display font-bold text-xl md:text-2xl text-[#2D2622] mt-1">Purchase Order</div>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 md:p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="PO Number">
              <input required data-testid="po-no" value={f.po_no}
                onChange={(e) => setF({ ...f, po_no: e.target.value })}
                className="h-11 px-3 border border-[#E5D8C4] w-full text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Estimate no (opt)">
              <input value={f.estimate_no} onChange={(e) => setF({ ...f, estimate_no: e.target.value })}
                className="h-11 px-3 border border-[#E5D8C4] w-full text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Billing company">
              <SearchableSelect testId="po-billing" value={f.billing_company_id}
                onChange={(v) => setF({ ...f, billing_company_id: v })}
                options={bills.map(b => ({ value: b.id, label: b.name }))} />
            </Field>
            <Field label="Order date">
              <input type="date" value={f.order_date} onChange={(e) => setF({ ...f, order_date: e.target.value })}
                className="h-11 px-3 border border-[#E5D8C4] w-full text-sm focus:outline-none focus:border-[#2D2622]" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Expected delivery">
              <input type="date" value={f.expected_delivery}
                onChange={(e) => setF({ ...f, expected_delivery: e.target.value })}
                className="h-11 px-3 border border-[#E5D8C4] w-full text-sm focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Notes / packing instructions">
              <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                data-testid="po-notes"
                placeholder="Line 1: brand-side instructions&#10;Line 2: dispatch remarks"
                className="px-3 py-2 border border-[#E5D8C4] w-full text-sm focus:outline-none focus:border-[#2D2622] resize-y min-h-[80px]" />
            </Field>
          </div>
          {customFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#F3ECDF]">
              {customFields.map(cf => (
                <Field key={cf.id} label={`${cf.label}${cf.required ? " *" : ""}`}>
                  <POCustomFieldInput def={cf}
                    value={f.custom_fields?.[cf.key]}
                    onChange={(v) => setF(x => ({ ...x, custom_fields: { ...(x.custom_fields || {}), [cf.key]: v }}))} />
                </Field>
              ))}
            </div>
          )}
          <div className="pt-3 border-t border-[#F3ECDF]">
            <div className="flex justify-between items-center mb-3">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Order lines</div>
              <button onClick={addLine} data-testid="po-add-line"
                className="h-8 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:border-[#2D2622]">
                <Plus size={12}/> Add line
              </button>
            </div>
            <div className="space-y-3">
              {f.lines.map((line, i) => (
                <POLineEditor key={line._key || i} cats={cats} line={line} idx={i}
                  onChange={(nl) => { const lines = [...f.lines]; lines[i] = nl; setF({ ...f, lines }); }}
                  onAddSize={() => addSizeFromLine(i)}
                  onRemove={() => rmLine(i)} />
              ))}
              {f.lines.length === 0 && (
                <div className="border border-dashed border-[#E5D8C4] p-6 text-center text-xs text-[#8B6F47]">
                  Add a line to specify what's ordered.
                </div>
              )}
              {/* Second "Add line" trigger right under the last line so the
                  operator doesn't have to scroll back to the top after adding
                  many rows. */}
              {f.lines.length > 0 && (
                <button onClick={addLine} data-testid="po-add-line-bottom"
                  className="w-full h-11 border border-dashed border-[#8B6F47] text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 text-[#5D4A2E] hover:bg-[#F3ECDF]">
                  <Plus size={14}/> Add another product line
                </button>
              )}
            </div>
          </div>

          {/* Warehouse assignments — filter assignees by warehouse designation */}
          {usedWarehouses.length > 0 && (
            <div className="pt-3 border-t border-[#F3ECDF]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">
                Warehouse assignees
              </div>
              <div className="space-y-2">
                {usedWarehouses.map(wh => {
                  const cur = f.warehouse_assignments?.find(w => w.warehouse_place === wh);
                  const ids = cur?.assignee_ids || [];
                  // Only show users designated to this warehouse (or admins with no scope)
                  const eligible = assignees.filter(u =>
                    !u.warehouse_ids || u.warehouse_ids.length === 0 || u.warehouse_ids.includes(wh)
                  );
                  return (
                    <div key={wh} className="p-3 border border-[#E5D8C4] bg-[#FAF7F2]">
                      <div className="text-sm font-semibold text-[#2D2622] mb-2">{wh}</div>
                      {eligible.length === 0 && (
                        <div className="text-[11px] text-[#C2410C]">No user is designated to this warehouse. Assign warehouses in Settings › Users.</div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {eligible.map(u => {
                          const sel = ids.includes(u.id);
                          return (
                            <button type="button" key={u.id}
                              onClick={() => setAssignForWH(wh, sel ? ids.filter(x => x !== u.id) : [...ids, u.id])}
                              data-testid={`assign-${wh.replace(/\s+/g,'-')}-${u.id}`}
                              className={`px-3 py-1.5 text-xs border ${sel?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                              {u.full_name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-[#FAF7F2] border-t border-[#F3ECDF] flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="po-save"
            className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#5D4A2E] disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14}/> Save PO</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------- Line editor with lot allocation ------------- */
function POLineEditor({ cats, line, idx, onChange, onAddSize, onRemove }) {
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);

  useEffect(() => {
    if (!line.category_id) { setColors([]); setSizes([]); return; }
    api.get("/colors", { params: { category_id: line.category_id } }).then(r => setColors(r.data));
    api.get("/sizes", { params: { category_id: line.category_id } }).then(r => setSizes(r.data));
  }, [line.category_id]);

  useEffect(() => {
    if (!line.category_id || !line.color_id || !line.size_id) { setAvailableLots([]); return; }
    // /pack-candidates returns exact-match lots AND any larger-size lots
    // in the same category+colour that can be trimmed down to fit
    // (both dimensions ≥ the ordered size). Cut-candidates carry
    // `can_cut: true` + `cut_from_size_label` so we can badge them.
    api.get("/pack-candidates", { params: {
      category_id: line.category_id, color_id: line.color_id, size_id: line.size_id
    }}).then(r => setAvailableLots(r.data));
  }, [line.category_id, line.color_id, line.size_id]);

  const setAlloc = (lotId, qty) => {
    const allocations = [...(line.allocations || [])];
    const i = allocations.findIndex(a => a.lot_id === lotId);
    // Prevent typing a per-lot value that would push the line total past the
    // ordered qty. Silently clamp instead of erroring so users can adjust.
    const otherAlloc = allocations.reduce((s, a, idx) =>
      s + (idx === i ? 0 : (a.quantity || 0)), 0);
    const room = Math.max(0, (line.quantity || 0) - otherAlloc);
    const capped = Math.max(0, Math.min(qty, room));
    if (capped <= 0) { if (i >= 0) allocations.splice(i, 1); }
    else if (i >= 0) allocations[i] = { lot_id: lotId, quantity: capped };
    else allocations.push({ lot_id: lotId, quantity: capped });
    onChange({ ...line, allocations });
  };
  const totalAlloc = (line.allocations || []).reduce((s, a) => s + a.quantity, 0);
  const overAlloc = totalAlloc > (line.quantity || 0);

  return (
    <div className="border border-[#E5D8C4] p-3 md:p-4 bg-[#FAF7F2]">
      <div className="flex justify-between items-start mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">Line {idx + 1}</div>
        <button onClick={onRemove} className="text-[#8B6F47] hover:text-red-600"><X size={14}/></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <SearchableSelect testId={`po-line-${idx}-cat`} value={line.category_id}
          onChange={(v) => onChange({ ...line, category_id: v, color_id: "", size_id: "", allocations: [] })}
          options={cats.map(c => ({ value: c.id, label: c.name }))} placeholder="Category" />
        <SearchableSelect testId={`po-line-${idx}-color`} value={line.color_id}
          onChange={(v) => onChange({ ...line, color_id: v, allocations: [] })}
          options={colors.map(c => ({ value: c.id, label: c.name }))}
          placeholder="Colour" disabled={!line.category_id} />
        <SearchableSelect testId={`po-line-${idx}-size`} value={line.size_id}
          onChange={(v) => onChange({ ...line, size_id: v, allocations: [] })}
          options={sizes.map(s => ({ value: s.id, label: s.label }))}
          placeholder="Size" disabled={!line.category_id} />
        <input type="number" min="1" value={line.quantity}
          placeholder="Qty"
          onChange={(e) => onChange({ ...line, quantity: e.target.value })}
          data-testid={`po-line-${idx}-qty`}
          className="h-11 px-3 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
      </div>

      {/* Per-line notes — printed on the PO/Packing list next to the line */}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">
          Item notes <span className="normal-case tracking-normal text-[#A8998A]">· optional</span>
        </div>
        <textarea rows={2} value={line.notes || ""}
          onChange={(e) => onChange({ ...line, notes: e.target.value })}
          data-testid={`po-line-${idx}-notes`}
          placeholder="Finish, packing, dispatch remarks for this product..."
          className="w-full px-3 py-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] resize-y" />
      </div>

      {/* "Add another size" — clones this line's category + colour so multiple
          sizes for the same material can be entered without re-selecting both
          dropdowns every time. */}
      {line.category_id && line.color_id && (
        <div className="mt-3">
          <button type="button" onClick={onAddSize}
            data-testid={`po-line-${idx}-add-size`}
            className="w-full h-10 border border-dashed border-[#8B6F47] text-[11px] uppercase tracking-[0.2em] text-[#5D4A2E] inline-flex items-center justify-center gap-2 hover:bg-[#F3ECDF]">
            + Add another size · same category &amp; colour
          </button>
        </div>
      )}
      {line.category_id && line.color_id && line.size_id && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-2">
            Allocate · <span className={overAlloc ? "text-red-700 font-bold" : ""}>{totalAlloc}/{line.quantity}</span> pcs
            {totalAlloc === 0 && <span className="ml-2 text-[#C2410C]">— pack later when stock arrives</span>}
            {overAlloc && (
              <span data-testid={`po-line-${idx}-over`} className="ml-2 text-red-700 font-bold normal-case tracking-normal">
                — allocated quantity exceeds ordered quantity
              </span>
            )}
          </div>
          {availableLots.length === 0 ? (
            <div className="text-xs text-[#8B6F47] italic p-2 bg-white border border-[#E5D8C4]">
              No lots available yet. PO will be created and packed when stock arrives.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableLots.map(lot => {
                const alloc = (line.allocations || []).find(a => a.lot_id === lot.id);
                return (
                  <div key={lot.id}
                    data-testid={`po-line-${idx}-lot-${lot.lot_no}`}
                    className={`flex justify-between items-center gap-3 p-2 border ${lot.can_cut ? "bg-[#FFF7E6] border-[#E0B36A]" : "bg-white border-[#E5D8C4]"}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-mono2 font-semibold text-[#2D2622] truncate">{lot.lot_no} · {lot.rack_no}</div>
                      <div className="text-[10px] text-[#8B6F47]">
                        avail {lot.quantity} pcs · <span className="font-mono2">{lot.size_label}</span>
                      </div>
                      {lot.can_cut && (
                        <div data-testid={`po-line-${idx}-cut-badge-${lot.lot_no}`}
                          className="mt-1 inline-block text-[9px] font-bold uppercase tracking-[0.15em] bg-[#C2410C] text-white px-1.5 py-0.5">
                          ✂ Cut · {lot.size_label} → {lot.ordered_size_label}
                        </div>
                      )}
                    </div>
                    <input type="number" min="0" max={lot.quantity} value={alloc?.quantity || 0}
                      onChange={(e) => setAlloc(lot.id, parseInt(e.target.value || "0", 10))}
                      className="w-16 h-8 px-2 border border-[#E5D8C4] font-mono2 text-center text-sm focus:outline-none focus:border-[#2D2622]" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------- Detail view + pack/dispatch ------------- */
function PODetail({ poId, onClose, onChanged }) {
  const { can, user } = useAuth();
  const [po, setPo] = useState(null);
  const [dispatchConfirm, setDispatchConfirm] = useState(false);
  const [undispatchConfirm, setUndispatchConfirm] = useState(false);
  const [packing, setPacking] = useState(null); // { lineIdx }
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get(`/purchase-orders/${poId}`).then(r => { setPo(r.data); onChanged?.(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [poId]);

  const dispatch = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${poId}/dispatch`, {
        employee_name: user?.full_name || user?.email });
      toast.success(`PO ${po.po_no} dispatched · stock updated`);
      setDispatchConfirm(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Admin-only undo: restocks every OUT this dispatch made and reopens the PO.
  const undispatch = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${poId}/undispatch`);
      toast.success(`PO reopened · restocked ${r.data.reversed_qty} pcs across ${r.data.reversed_lots} lot${r.data.reversed_lots === 1 ? "" : "s"}`);
      setUndispatchConfirm(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Print with a dynamic filename (browsers use document.title as the default
  // "Save as PDF" name). We temporarily override, print, then restore.
  const printPO = () => {
    const original = document.title;
    document.title = `${po.po_no} - Purchase Order`;
    // Restore once the print dialog closes — supported in every modern browser
    // via the afterprint event. Fallback timer in case a browser skips the event.
    const restore = () => { document.title = original;
      window.removeEventListener("afterprint", restore); };
    window.addEventListener("afterprint", restore);
    setTimeout(restore, 60_000);
    window.print();
  };

  // ---- Multi-warehouse fulfillment scope for the current user ----
  // Non-admins with warehouse_ids see the whole PO for context, but the
  // Pack button is only enabled for lines that either touch one of their
  // warehouses or have no warehouse mapping yet (unassigned lots stay open).
  const userWhs = user?.role === "admin" ? null : (user?.warehouse_ids || []);
  const canPackLine = (line) => {
    if (!userWhs || userWhs.length === 0) return true; // admin or unrestricted
    const allocs = line.allocations || [];
    if (allocs.length === 0) return true; // unmapped line — open to any assigned user
    const anyMine = allocs.some(a => !a.warehouse_place || userWhs.includes(a.warehouse_place));
    return anyMine;
  };

  if (!po) return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={onClose}>
      <div className="text-white">Loading…</div>
    </div>
  );

  // Group allocations by warehouse for per-warehouse packing pages
  const byWarehouse = {};
  po.lines?.forEach((line, li) => {
    (line.allocations || []).forEach(a => {
      const wh = a.warehouse_place || "Unassigned";
      byWarehouse[wh] = byWarehouse[wh] || {};
      const key = `${line.category_name}|${line.color_name}|${line.size_label}`;
      byWarehouse[wh][key] = byWarehouse[wh][key] || {
        category_name: line.category_name, color_name: line.color_name,
        size_label: line.size_label, allocs: []
      };
      byWarehouse[wh][key].allocs.push({ ...a, line_no: li + 1 });
    });
  });

  const totalPacked = (line) => (line.packs || []).reduce((s, p) => s + (p.quantity || 0), 0);
  const totalOrdered = po.lines?.reduce((s, l) => s + l.quantity, 0) || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-2 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl border border-[#E5D8C4] my-4" onClick={(e)=>e.stopPropagation()}>
        <div className="p-4 md:p-5 border-b border-[#F3ECDF] flex justify-between items-start no-print flex-wrap gap-3 bg-white">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">{po.billing_company_name}</div>
            <div className="font-display font-bold text-2xl md:text-3xl text-[#2D2622] mt-1">{po.po_no}</div>
            <div className="text-xs font-mono2 text-[#5D5148] mt-1">
              {po.estimate_no && `Est. ${po.estimate_no} · `}
              Ordered {po.order_date} · Due {po.expected_delivery || "—"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold ${STATUS_STYLES[po.status]}`}>
              {po.status}
            </div>
            <button onClick={printPO} data-testid="po-print"
              className="h-9 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:border-[#2D2622]">
              <FileText size={13}/> Print PDF
            </button>
            <button onClick={onClose} className="p-2"><X size={16}/></button>
          </div>
        </div>

        {/* PDF-style body — natural pagination across multiple pages.
            Fixed heights + overflow are stripped in print so long tables
            (many colours, many warehouses) don't get clipped at the bottom
            of page 1. Each per-warehouse packing sheet on a multi-WH order
            starts on a new page via .warehouse-packing-sheet. */}
        <div id="print-area" className="po-print-container p-4 md:p-6 space-y-3">
          <style>{`
            @media print {
              /* Standard A4 portrait, generous side/top margins */
              @page { size: A4 portrait; margin: 12mm 10mm 12mm 10mm; }

              /* Un-clip every viewport-based container so content flows across pages */
              html, body, #root, .po-print-container {
                height: auto !important;
                max-height: none !important;
                overflow: visible !important;
                background: white !important;
              }
              /* Isolate the print area — everything else is hidden */
              body * { visibility: hidden; }
              #print-area, #print-area * { visibility: visible; }
              #print-area { position: absolute; left: 0; top: 0; width: 100%; }
              #print-area .no-print { display: none !important; }

              /* Modal wrappers around the print area shouldn't cap the height */
              .fixed.inset-0 {
                position: static !important;
                height: auto !important;
                max-height: none !important;
                overflow: visible !important;
                background: transparent !important;
                padding: 0 !important;
              }
              .fixed.inset-0 > .bg-white {
                max-height: none !important;
                height: auto !important;
                overflow: visible !important;
                border: none !important;
                margin: 0 !important;
              }

              /* Natural pagination on tables — no clipping, header repeats on
                 every printed page, rows never split down the middle. */
              #print-area table { page-break-inside: auto !important; width: 100% !important; }
              #print-area thead { display: table-header-group !important; }
              #print-area tfoot { display: table-footer-group !important; }
              #print-area tr    { page-break-inside: avoid !important;
                                  page-break-after: auto !important; }

              /* Multi-warehouse packing pages: each starts on its own page */
              #print-area .warehouse-packing-sheet {
                page-break-before: always !important;
                break-before: page !important;
              }
            }
          `}</style>
          <POPrintable po={po} />
          {/* Per-warehouse packing pages */}
          {(() => {
            const entries = Object.entries(byWarehouse);
            // Per requirements: Single warehouse → same continuous page.
            // 2+ warehouses → each packing list starts on its own dedicated page.
            const splitPages = entries.length >= 2;
            return entries.map(([wh, keys]) => {
            const assignment = (po.warehouse_assignments || []).find(w => w.warehouse_place === wh);
            const assigneeNames = (assignment?.assignee_names || []).join(", ");
            return (
            <div key={wh} className={`wh-page pt-4 ${splitPages ? "warehouse-packing-sheet" : ""}`}>
              <div className="text-center border-2 border-black py-2 mb-3">
                <div className="font-black text-lg">PACKING LIST — {wh.toUpperCase()}</div>
                <div className="text-xs">PO {po.po_no} · {po.billing_company_name}</div>
                {assigneeNames && (
                  <div className="text-xs mt-1"><b>Assigned to:</b> {assigneeNames}</div>
                )}
              </div>
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-[#F3ECDF]">
                    <th className="border border-black p-1 text-left">Category</th>
                    <th className="border border-black p-1 text-left">Colour</th>
                    <th className="border border-black p-1 text-left">Size</th>
                    <th className="border border-black p-1 text-left">Lot</th>
                    <th className="border border-black p-1 text-left">Rack</th>
                    <th className="border border-black p-1 text-right">Qty</th>
                    <th className="border border-black p-1 text-left">Cutting</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(keys).flatMap((k, ki) => k.allocs.map((a, ai) => (
                    <tr key={`${ki}-${ai}`}>
                      <td className="border border-black p-1">{k.category_name}</td>
                      <td className="border border-black p-1 font-semibold">{k.color_name}</td>
                      <td className="border border-black p-1 font-mono2">{k.size_label}</td>
                      <td className="border border-black p-1 font-mono2">{a.lot_no}</td>
                      <td className="border border-black p-1 font-mono2 font-bold">{a.rack_no}</td>
                      <td className="border border-black p-1 text-right font-mono2 font-bold">{a.quantity}</td>
                      <td className="border border-black p-1 text-[10px] font-mono2 font-bold text-[#C2410C]">
                        {a.cut_notice ? `✂ ${a.cut_notice}` : ""}
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          );});})()}
        </div>

        {/* Packing workflow */}
        <div className="p-4 md:p-6 border-t border-[#F3ECDF] no-print space-y-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Packing progress</div>
          {po.lines?.map((line, i) => {
            const packed = totalPacked(line);
            const remaining = Math.max(0, line.quantity - packed);
            const isDone = remaining === 0 && line.quantity > 0;
            return (
              <div key={i} className={`border p-3 md:p-4 ${isDone ? "bg-green-50 border-green-300" : "bg-white border-[#E5D8C4]"}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">Line {i+1} · {line.category_name}</div>
                    <div className="font-display font-semibold text-[#2D2622] mt-1">{line.color_name} · {line.size_label}</div>
                    <div className="text-xs text-[#5D5148] mt-1 font-mono2">
                      Ordered <b>{line.quantity}</b> · Packed <b>{packed}</b> ·
                      Remaining <b className={remaining ? "text-[#C2410C]" : "text-green-700"}>{remaining}</b>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-32 h-2 bg-[#F3ECDF] mt-1">
                      <div className={`h-full ${isDone?"bg-green-600":"bg-[#8B6F47]"}`}
                        style={{ width: `${Math.min(100, line.quantity ? packed*100/line.quantity : 0)}%` }} />
                    </div>
                  </div>
                </div>
                {(line.allocations || []).some(a => a.cut_notice) && (
                  <div className="mt-2 space-y-1">
                    {line.allocations.filter(a => a.cut_notice).map((a, ci) => (
                      <div key={ci} data-testid={`po-cut-notice-${i}-${ci}`}
                        className="text-[11px] font-bold font-mono2 text-white bg-[#C2410C] inline-block px-2 py-0.5 mr-1">
                        ✂ {a.cut_notice}
                      </div>
                    ))}
                  </div>
                )}
                {(line.packs || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#F3ECDF] space-y-1">
                    {line.packs.map((p, pi) => (
                      <div key={p.id || `pack-${pi}`} className="text-xs font-mono2 text-[#5D5148] flex justify-between items-center gap-2">
                        <span>Pack #{pi+1} · <b className="text-[#2D2622]">{p.quantity} pcs</b> · {p.packed_by}</span>
                        <span className="flex items-center gap-1">
                          {(p.photos || []).length > 0 && <span className="text-green-700">📸 {p.photos.length}</span>}
                          <span className="text-[10px]">{new Date(p.packed_at).toLocaleString()}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {can("po.pack") && remaining > 0 && po.status !== "dispatched" && (
                  <div className="mt-3 pt-3 border-t border-[#F3ECDF]">
                    {canPackLine(line) ? (
                      <button onClick={() => setPacking({ lineIdx: i, remaining })}
                        data-testid={`po-pack-line-${i}`}
                        className="h-9 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-widest inline-flex items-center gap-2">
                        <Package size={13}/> Pack ({remaining} left)
                      </button>
                    ) : (
                      <div data-testid={`po-line-locked-${i}`}
                        className="inline-flex items-center gap-2 h-9 px-3 bg-[#F3ECDF] border border-[#E5D8C4] text-[10px] uppercase tracking-widest text-[#8B6F47]">
                        🔒 Assigned to another warehouse — read only
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {can("po.dispatch") && po.status !== "dispatched" && (
          <div className="p-4 bg-[#FAF7F2] border-t border-[#F3ECDF] flex justify-between items-center gap-2 no-print flex-wrap">
            <div className="text-xs text-[#5D5148]">
              {po.lines.every(l => totalPacked(l) >= l.quantity)
                ? "✅ All lines fully packed. Ready to dispatch."
                : "⚠️ Some lines still have remaining qty. Confirm before dispatch."}
            </div>
            <button onClick={() => setDispatchConfirm(true)} data-testid="po-dispatch-btn"
              className="h-11 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#5D4A2E]">
              <Truck size={14}/> Dispatch & reduce stock
            </button>
          </div>
        )}

        {/* Admin-only "Undo dispatch". Non-admins never see this button so the
            PO stays permanently closed to them once dispatched. */}
        {po.status === "dispatched" && user?.role === "admin" && (
          <div className="p-4 border-t border-[#F3ECDF] flex items-center justify-between gap-3 flex-wrap no-print"
               style={{ background: "#FFF3CD" }}>
            <div className="text-xs text-[#5D4A2E] max-w-xl">
              <b>Admin only:</b> Reopen this PO to edit quantities/colours or delete it.
              Every dispatched quantity is added back to the source lots and a matching
              IN movement is logged for the audit trail.
            </div>
            <button onClick={() => setUndispatchConfirm(true)} data-testid="po-undispatch-btn"
              className="h-11 px-5 bg-[#5D4A2E] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#2D2622]">
              ↩ Undo dispatch &amp; restock
            </button>
          </div>
        )}
      </div>

      {packing && (
        <PackingDialog po={po} lineIdx={packing.lineIdx}
          onClose={() => setPacking(null)}
          onSaved={() => { setPacking(null); refresh(); }} />
      )}

      <ConfirmDialog open={dispatchConfirm} onCancel={() => setDispatchConfirm(false)}
        onConfirm={dispatch} loading={busy} tone="danger"
        title="Dispatch entire PO?"
        message={<>This will reduce stock for every allocated lot on <b>{po.po_no}</b> and log OUT movements. Continue?</>}
        confirmLabel="Yes, dispatch now" />

      <ConfirmDialog open={undispatchConfirm} onCancel={() => setUndispatchConfirm(false)}
        onConfirm={undispatch} loading={busy} tone="danger"
        title="Undo dispatch and restock?"
        message={<>Every quantity dispatched under <b>{po.po_no}</b> will be added back to its source lot and the PO will return to <b>draft</b> so it can be edited or deleted. This action is logged and cannot itself be undone. Continue?</>}
        confirmLabel="Yes, undo dispatch" />
    </div>
  );
}

/* ------------- PDF/print template matching the reference screenshot ------------- */
function POPrintable({ po }) {
  const [layout, setLayout] = useState({
    company_name: "", company_address: "", company_gstin: "",
    company_phone: "", company_email: "", company_logo_url: "",
    header_text: "Veneer Slate · Flexible Stone Veneer",
    header_style: "bordered",
    accent_color: "#2D2622",
    paper_size: "A4",
    column_order: ["sr", "colour", "size", "quantity"],
    show_expected_delivery: true,
    show_notes_block: true,
    show_lot_allocation: true,
    show_notes_column: true,
    show_grand_total: true,
    show_unallocated_callout: true,
    unallocated_placement: "top",
    show_signature_block: false,
    signature_label: "Authorised signatory",
    terms_text: "",
    footer_text: "",
  });
  useEffect(() => {
    api.get("/po-layout-settings").then(r => setLayout(l => ({ ...l, ...r.data })))
      .catch(() => {});
  }, []);
  const accent = layout.accent_color || "#2D2622";
  const useAccent = layout.header_style === "accent";
  // Original B&W look: black borders, cream fills. Only switch when admin
  // explicitly picks the "accent" header style.
  const bandBg   = useAccent ? accent           : "#F3ECDF";
  const bandFg   = useAccent ? "#FFFFFF"        : "#000000";
  const rowStripeBg = "#FAF7F2";
  const headerBorder = useAccent ? `2px solid ${accent}` : "2px solid #000";

  // Group lines by category, then columns = distinct sizes, rows = colours
  const groups = useMemo(() => {
    const g = {};
    (po.lines || []).forEach((l, li) => {
      const k = l.category_name;
      if (!g[k]) g[k] = { name: k, sizes: new Set(), byColor: {}, lines: [] };
      g[k].sizes.add(l.size_label);
      const c = g[k].byColor[l.color_name] || (g[k].byColor[l.color_name] = {});
      const prev = c[l.size_label] || { qty: 0, unalloc: false };
      const hasAlloc = (l.allocations || []).length > 0;
      c[l.size_label] = { qty: prev.qty + l.quantity, unalloc: prev.unalloc || !hasAlloc };
      g[k].lines.push({ ...l, line_no: li + 1 });
    });
    return Object.values(g).map(x => ({
      name: x.name, sizes: [...x.sizes], byColor: x.byColor, lines: x.lines,
    }));
  }, [po.lines]);

  const unallocatedLines = (po.lines || []).filter(l => (l.allocations || []).length === 0);
  const showTopUnalloc  = layout.show_unallocated_callout &&
    (layout.unallocated_placement === "top" || layout.unallocated_placement === "both");
  const showGroupUnalloc = layout.show_unallocated_callout &&
    (layout.unallocated_placement === "per_group" || layout.unallocated_placement === "both");
  const totalOrdered = (po.lines || []).reduce((s, l) => s + (l.quantity || 0), 0);

  return (
    <div className="text-black" style={{ fontFamily: "Arial, sans-serif" }}>
      {/* Page size override — the outer print CSS defaults to A4; we only
          override when the admin picks A5 or Letter. */}
      {layout.paper_size !== "A4" && (
        <style>{`@media print { @page { size: ${layout.paper_size} portrait; } }`}</style>
      )}

      {/* Header — original B&W by default, coloured bar when header_style="accent" */}
      <div className="text-center py-2 mb-3"
           style={ useAccent
             ? { background: accent, color: "white", border: headerBorder }
             : { border: headerBorder } }>
        <div className="font-black text-2xl tracking-wide">ORDER</div>
      </div>

      {/* Company block (only rendered when at least one field is filled) */}
      {(layout.company_name || layout.company_logo_url || layout.company_address ||
        layout.company_gstin || layout.company_phone || layout.company_email) && (
        <div className="mb-3 flex items-start gap-3 border border-black p-2">
          {layout.company_logo_url && (
            <img src={layout.company_logo_url} alt="logo"
                 style={{ maxHeight: 64, maxWidth: 120, objectFit: "contain" }} />
          )}
          <div className="text-[11px] leading-tight">
            {layout.company_name && <div className="font-black text-sm">{layout.company_name}</div>}
            {layout.company_address && (
              <div className="whitespace-pre-line">{layout.company_address}</div>
            )}
            <div>
              {layout.company_phone && <span>Tel: {layout.company_phone}</span>}
              {layout.company_phone && layout.company_email && <span> · </span>}
              {layout.company_email && <span>{layout.company_email}</span>}
            </div>
            {layout.company_gstin && <div><b>GSTIN:</b> {layout.company_gstin}</div>}
          </div>
        </div>
      )}

      <div className="mb-4 text-center">
        <div className="font-bold text-lg">{po.billing_company_name}</div>
        <div className="text-[11px]">{layout.header_text}</div>
      </div>

      {/* Meta panel */}
      <div className={`grid ${layout.show_expected_delivery ? "grid-cols-2" : "grid-cols-1"} mb-3 gap-2 text-xs`}>
        <div className="border border-black">
          <div className="px-2 py-1 border-b border-black"><b>Date:</b> {po.order_date}</div>
          <div className="px-2 py-1 border-b border-black"><b>PO NUMBER -</b> {po.po_no}</div>
          {po.estimate_no && <div className="px-2 py-1 border-b border-black"><b>ESTIMATE -</b> {po.estimate_no}</div>}
          {layout.show_notes_block && (
            <div className="px-2 py-1 whitespace-pre-line"><b>NOTES:</b> {po.notes || "—"}</div>
          )}
        </div>
        {layout.show_expected_delivery && (
          <div className="border border-black">
            <div className="px-2 py-1 border-b border-black text-center font-bold">EXPECTED DELIVERY DATE</div>
            <div className="px-2 py-1 text-center font-mono2 text-base">{po.expected_delivery || "—"}</div>
          </div>
        )}
      </div>

      {showTopUnalloc && unallocatedLines.length > 0 && (
        <div className="mb-3 border-2 border-black p-2 text-[11px]" style={{ background: "#FFF3CD" }}>
          <b>NEED TO BE PRODUCED · {unallocatedLines.length} line{unallocatedLines.length === 1 ? "" : "s"}:</b>{" "}
          {unallocatedLines.map((l, i) => (
            <span key={`unalloc-${l.line_no ?? i}-${l.color_id ?? ""}-${l.size_id ?? ""}`}>
              {i > 0 ? "; " : ""}{l.category_name} · {l.color_name} · {l.size_label} × {l.quantity}
            </span>
          ))}
        </div>
      )}

      {groups.length === 0 && <div className="italic text-center py-6">No order lines.</div>}

      {groups.map((g) => {
        const colours = Object.keys(g.byColor);
        const totals = {};
        g.sizes.forEach(s => {
          totals[s] = colours.reduce((sum, c) => sum + ((g.byColor[c][s]?.qty) || 0), 0);
        });
        const grandTotal = Object.values(totals).reduce((a,b)=>a+b, 0);
        const groupUnallocatedLines = g.lines.filter(l => (l.allocations || []).length === 0);
        return (
          <div key={g.name} style={{ pageBreakInside: "avoid" }} className="mb-4">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th colSpan={2 + g.sizes.length}
                      className="border border-black text-center py-1"
                      style={{ background: bandBg, color: bandFg }}>
                    <b>ORDER DETAILS — {g.name.toUpperCase()}</b>
                  </th>
                </tr>
                <tr>
                  <th className="border border-black p-1 w-8">#</th>
                  <th className="border border-black p-1 text-left">COLOUR</th>
                  {g.sizes.map((s) => (
                    <th key={s} className="border border-black p-1 text-center">
                      <div className="font-normal text-[10px]">{s}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colours.map((color, ci) => (
                  <tr key={color}>
                    <td className="border border-black p-1 text-center">{ci + 1}</td>
                    <td className="border border-black p-1 font-semibold">{color.toUpperCase()}</td>
                    {g.sizes.map(s => {
                      const cell = g.byColor[color][s];
                      if (!cell) return <td key={s} className="border border-black p-1 text-center"></td>;
                      return (
                        <td key={s} className="border border-black p-1 text-center font-bold">
                          {cell.qty}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {layout.show_grand_total && (
                  <tr className="font-bold" style={{ background: rowStripeBg }}>
                    <td className="border border-black p-1"></td>
                    <td className="border border-black p-1">
                      TOTAL {g.name.toUpperCase()} · <span className="font-mono2">{grandTotal} PCS</span>
                    </td>
                    {g.sizes.map(s => (
                      <td key={s} className="border border-black p-1 text-center">{totals[s] || 0}</td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>

            {layout.show_notes_column && g.lines.some(l => (l.notes || "").trim()) && (
              <div className="mt-2 border border-black text-[11px]">
                <div className="px-2 py-1 border-b border-black font-bold"
                     style={{ background: bandBg, color: bandFg }}>
                  ITEM NOTES — {g.name.toUpperCase()}
                </div>
                {g.lines.filter(l => (l.notes || "").trim()).map((l) => (
                  <div key={l.line_no} className="px-2 py-1 border-b border-gray-300 whitespace-pre-line">
                    <b>L{l.line_no} · {l.color_name} · {l.size_label}:</b> {l.notes}
                  </div>
                ))}
              </div>
            )}

            {showGroupUnalloc && groupUnallocatedLines.length > 0 && (
              <div className="mt-2 border-2 border-black text-[11px]" style={{ background: "#FFF3CD" }}>
                <div className="px-2 py-1 border-b border-black font-black">
                  NEED TO BE PRODUCED — {g.name.toUpperCase()}
                </div>
                <table className="w-full">
                  <tbody>
                    {groupUnallocatedLines.map(l => (
                      <tr key={l.line_no}>
                        <td className="px-2 py-1 border-b border-gray-400 font-mono2">L{l.line_no}</td>
                        <td className="px-2 py-1 border-b border-gray-400">
                          {l.color_name} · {l.size_label}
                        </td>
                        <td className="px-2 py-1 border-b border-gray-400 text-right font-mono2 font-bold">
                          {l.quantity} PCS
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {layout.show_lot_allocation && g.lines.some(l => (l.allocations || []).length > 0) && (
              <div className="mt-2 border border-black text-[11px]">
                <div className="px-2 py-1 border-b border-black font-bold"
                     style={{ background: bandBg, color: bandFg }}>
                  LOT ALLOCATION — {g.name.toUpperCase()}
                </div>
                <table className="w-full">
                  <thead>
                    <tr style={{ background: rowStripeBg }}>
                      <th className="border-b border-black p-1 text-left">Line</th>
                      <th className="border-b border-black p-1 text-left">Colour · Size</th>
                      <th className="border-b border-black p-1 text-left">Lot</th>
                      <th className="border-b border-black p-1 text-left">Rack</th>
                      <th className="border-b border-black p-1 text-left">Warehouse</th>
                      <th className="border-b border-black p-1 text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.lines.flatMap(l => (l.allocations || []).map((a, ai) => (
                      <tr key={`${l.line_no}-${ai}`}>
                        <td className="border-b border-gray-400 p-1">L{l.line_no}</td>
                        <td className="border-b border-gray-400 p-1">{l.color_name} · {l.size_label}</td>
                        <td className="border-b border-gray-400 p-1 font-mono2">{a.lot_no}</td>
                        <td className="border-b border-gray-400 p-1 font-mono2">{a.rack_no}</td>
                        <td className="border-b border-gray-400 p-1">{a.warehouse_place || "—"}</td>
                        <td className="border-b border-gray-400 p-1 text-right font-mono2 font-bold">{a.quantity}</td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Grand total across all categories */}
      {layout.show_grand_total && groups.length > 1 && (
        <div className="border-2 border-black px-3 py-2 text-sm font-bold text-right mt-2">
          GRAND TOTAL · <span className="font-mono2">{totalOrdered} PCS</span>
        </div>
      )}

      {layout.terms_text && (
        <div className="mt-4 border border-black p-2 text-[10px] whitespace-pre-line">
          <div className="font-bold uppercase tracking-wider mb-1">Terms &amp; conditions</div>
          {layout.terms_text}
        </div>
      )}

      {layout.show_signature_block && (
        <div className="mt-6 flex justify-end">
          <div className="text-center text-[11px]">
            <div style={{ borderTop: "1px solid #000", width: 200, marginTop: 40 }} />
            <div className="mt-1">{layout.signature_label}</div>
          </div>
        </div>
      )}

      {layout.footer_text && (
        <div className="mt-4 text-center text-[10px] italic border-t border-black pt-2 whitespace-pre-line"
             style={{ color: useAccent ? accent : "#000" }}>
          {layout.footer_text}
        </div>
      )}
    </div>
  );
}

/* ------------- Detailed packing dialog ------------- */
function PackingDialog({ po, lineIdx, onClose, onSaved }) {
  const { user } = useAuth();
  const line = po.lines[lineIdx];
  const packed = (line.packs || []).reduce((s, p) => s + p.quantity, 0);
  const remainingBefore = Math.max(0, line.quantity - packed);
  const [availableLots, setAvailableLots] = useState([]);
  const [lotId, setLotId] = useState("");
  const [qty, setQty] = useState("");
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/lots", { params: {
      category_id: line.category_id, color_id: line.color_id, size_id: line.size_id
    }}).then(r => setAvailableLots(r.data));
  }, [line.category_id, line.color_id, line.size_id]);

  // Pre-select first allocated lot
  useEffect(() => {
    if (!lotId && (line.allocations || []).length > 0) {
      setLotId(line.allocations[0].lot_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("batch_no", po?.po_no || "NOBATCH");
      fd.append("stage", "po_line");
      fd.append("index", String((photos?.length || 0) + 1));
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(p => [...p, r.data.storage_path]);
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    const q = parseInt(qty, 10);
    if (!lotId || !q || q <= 0) { toast.error("Pick a lot and enter valid quantity"); return; }
    if (q > remainingBefore) { toast.error(`Only ${remainingBefore} pcs remaining to pack`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${po.id}/pack-detail`, {
        line_index: lineIdx, lot_id: lotId, quantity: q,
        photos, notes, employee_name: user?.full_name || user?.email,
      });
      toast.success(`Packed ${q} pcs · ${r.data.remaining} pcs remaining`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl border border-[#E5D8C4] my-4" onClick={(e)=>e.stopPropagation()}>
        <div className="p-4 md:p-5 border-b border-[#F3ECDF] flex justify-between items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Pack line {lineIdx + 1}</div>
            <div className="font-display font-bold text-xl text-[#2D2622] mt-1">{line.color_name} · {line.size_label}</div>
            <div className="text-xs text-[#5D5148] mt-1">
              {line.category_name} · Ordered <b>{line.quantity}</b> · Already packed <b>{packed}</b> ·
              Remaining <b className="text-[#C2410C]">{remainingBefore}</b>
            </div>
          </div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-4 md:p-5 space-y-4">
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Which lot are you packing from?</div>
            <SearchableSelect testId="pack-lot" value={lotId} onChange={setLotId}
              options={availableLots.map(l => ({
                value: l.id, label: `${l.lot_no} · Rack ${l.rack_no}`,
                sub: `avail ${l.quantity} pcs`
              }))} placeholder="Select the lot…" emptyMessage="No lots for this variant" />
          </div>
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Quantity being packed now</div>
            <input type="number" min="1" max={remainingBefore} value={qty} data-testid="pack-qty"
              onChange={(e) => setQty(e.target.value)}
              className="w-full h-12 px-3 border border-[#E5D8C4] font-mono2 text-2xl font-bold text-center focus:outline-none focus:border-[#2D2622]" />
            <div className="text-[11px] text-[#8B6F47] mt-1">
              Up to {remainingBefore} pcs remaining on this line.
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Packaging photos (multiple allowed)</div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={p || `pack-photo-${i}`} className="relative aspect-square border border-[#E5D8C4] bg-[#FAF7F2]">
                  <AuthImage path={p} className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(a => a.filter((_, x) => x !== i))}
                    className="absolute top-1 right-1 w-6 h-6 bg-white/90 flex items-center justify-center border border-[#E5D8C4]">
                    <X size={11}/>
                  </button>
                </div>
              ))}
              <label className="aspect-square border-2 border-dashed border-[#E5D8C4] flex items-center justify-center cursor-pointer hover:border-[#2D2622]">
                {uploading ? <Loader2 className="animate-spin" size={18}/> : <Camera size={20} className="text-[#8B6F47]"/>}
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  data-testid="pack-photo-camera" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
              <label className="aspect-square border-2 border-dashed border-[#E5D8C4] flex flex-col items-center justify-center cursor-pointer hover:border-[#2D2622]">
                <span className="text-[10px] uppercase tracking-widest text-[#8B6F47]">📁 File</span>
                <input type="file" accept="image/*" className="hidden"
                  data-testid="pack-photo-file" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Notes (optional)</div>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. wrapped in bubble wrap, box 1 of 3…"
              className="w-full px-3 py-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          </div>
        </div>
        <div className="p-4 bg-[#FAF7F2] border-t border-[#F3ECDF] flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest">Cancel</button>
          <button onClick={submit} disabled={busy || !lotId || !qty} data-testid="pack-submit"
            className="h-11 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#5D4A2E] disabled:opacity-40">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <><Package size={14}/> Confirm pack</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2 block">{label}</label>
      {children}
    </div>
  );
}

/** Dynamic custom-field input for the PO form. */
function POCustomFieldInput({ def, value, onChange }) {
  const base = "h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]";
  const testId = `po-custom-${def.key}`;
  if (def.field_type === "number") {
    return <input type="number" data-testid={testId} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className={`${base} font-mono2`} />;
  }
  if (def.field_type === "date") {
    return <input type="date" data-testid={testId} value={value || ""}
      onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (def.field_type === "dropdown") {
    return (
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
        <option value="">— select —</option>
        {(def.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (def.field_type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 h-11">
        <input type="checkbox" data-testid={testId} checked={!!value}
          onChange={(e) => onChange(e.target.checked)} />
        <span className="text-sm text-[#5D5148]">Yes</span>
      </label>
    );
  }
  return <input type="text" data-testid={testId} value={value || ""}
    onChange={(e) => onChange(e.target.value)} className={base} />;
}
